import React, { useEffect, useContext, useState, useCallback, Fragment } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { UserContext } from '../../../context/UserContext'
import { LoadingCover } from '../../commons/LoadingCover'
import { SystemError } from '../../error/SystemError'
import { Menu, Transition } from '@headlessui/react'
import { downloadStrings } from '../../../api/connections/connections-api'
import { StringsRemove } from './StringsRemove'
import { s3Static } from '../../../utils/constants'
import { openModal } from '../../../service/modal-service'
import { ChevronLeft, Search, MoreVertical, UserMinus, Users, Loader2 } from 'lucide-react'
import Link from 'next/link'

export const Strings = (props) => {
    const router = useRouter()
    const stringsUserId = props.stringsUserId

    const [ stringsArray, setStringsArray ] = useState([])
    const [ stringsArrayBackup, setStringsArrayBackup ] = useState([])
    const [ isLoadingStrings, setIsLoadingStrings ] = useState(true)

    const [ isShowConnectionRemove, setIsShowConnectionRemove ] = useState(false)

    const [ editConnectionId, setEditConnectionId ] = useState("")
    const [ searchText, setSearchText ] = useState("")

    const [ isLoading, setIsLoading ] = useState(false)
    const [ isSystemError, setIsSystemError ] = useState(false)
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}) 

    const { userId } = useContext(UserContext)

    const changeRoute = (path) =>{
        setIsLoading(true)
        router.push(path).then(() => {
            setIsLoading(false)
        })
    }

    const getMyStrings = useCallback(async() =>{      
        setIsLoadingStrings(true)  
        try {
            const response = await downloadStrings(stringsUserId)
            setStringsArray(response)
            setStringsArrayBackup(response)
        } catch (error) {
            console.log(error);
            setErrorContent({ 'header': "Error getting connections", 'body': "Please try again. If problem persists, contact us at support@stringssg.com" });
            setIsSystemError(true);
        }
        setIsLoadingStrings(false);
    }, [stringsUserId])


    useEffect(() => {
        if(!stringsUserId) return
        getMyStrings()
    }, [stringsUserId, getMyStrings])

    const removeConnection = (connectionId) => {
        setEditConnectionId(connectionId)
        openModal(setIsShowConnectionRemove)
    }

    const handleSearch = (e) => {
        e.preventDefault()
        setSearchText(e.target.value) 
        if(e.target.value.length > 2){
            setStringsArray(stringsArrayBackup.filter((list) => JSON.stringify(list).toLowerCase().indexOf(e.target.value.toLowerCase()) != -1))
        } else {
            setStringsArray(stringsArrayBackup)
        }    
    }

    return (
    <div className='bg-gray-50 min-h-screen'>
        {/* Header */}
        <div className='bg-white border-b border-gray-100'>
            <div className='flex items-center gap-3 px-4 py-3'>
                <h1 className='text-lg font-semibold text-gray-900'>Connections</h1>
                {stringsArray?.length > 0 && (
                    <span className='px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full'>
                        {stringsArray.length}
                    </span>
                )}
            </div>
        </div>

        <div className='pb-24'>
            {/* Search Bar */}
            <div className='p-4'>
                <div className='relative'>
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                        <Search size={20} className='text-gray-400' />
                    </div>
                    <input
                        type="search"
                        className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                        placeholder="Search connections..." 
                        name="searchText" 
                        value={searchText} 
                        onChange={handleSearch}
                    />
                </div>
            </div>   

            {/* Loading State */}
            {isLoadingStrings && (
                <div className='flex justify-center py-12'>
                    <Loader2 size={24} className='text-orange-500 animate-spin' />
                </div>
            )}

            {/* Empty State */}
            {!isLoadingStrings && stringsArray?.length === 0 && (
                <div className='text-center py-16'>
                    <div className='w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center'>
                        <Users size={32} className='text-gray-400' />
                    </div>
                    <p className='text-gray-500 font-medium'>No connections yet</p>
                    <p className='text-sm text-gray-400 mt-1'>Start connecting with people!</p>
                </div>
            )}

            {/* Connections List */}
            {!isLoadingStrings && stringsArray?.length > 0 && (
                <div className='px-4 space-y-2'>
                    {[...stringsArray].map((string, i) => (
                        <motion.div 
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className='bg-white rounded-2xl shadow-sm border border-gray-100 p-3'
                        >
                            <div className='flex items-center justify-between'>
                                <Link href={`/profile/${string.handleName}`} 
                                    className='flex items-center gap-3 flex-1 cursor-pointer' 
                                >
                                    <div className='relative h-12 w-12 shrink-0'>    
                                        <Image 
                                            className='rounded-xl'
                                            src={string.Image}
                                            fill
                                            style={{objectFit: 'cover'}}
                                            sizes="48px"
                                            placeholder="blur"
                                            blurDataURL={s3Static + "placeholder.png"}
                                            alt={string.fullName}
                                        />
                                    </div>
                                    <div className='min-w-0'>
                                        <h3 className='font-medium text-gray-900 truncate'>
                                            {string.fullName}
                                        </h3>
                                        <p className='text-xs text-gray-400'>Connected</p>
                                    </div>          
                                </Link> 
                                
                                {userId === stringsUserId && (
                                    <Menu as="div" className="relative">
                                        <Menu.Button className='p-2 hover:bg-gray-100 rounded-xl transition-colors'>
                                            <MoreVertical size={18} className='text-gray-400' />
                                        </Menu.Button>
                                        <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-200"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-150"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                        >
                                            <Menu.Items className='absolute right-0 mt-1 w-40 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden focus:outline-none z-10'>
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <button 
                                                            onClick={() => removeConnection(string.connectionId)}
                                                            className={`${active ? 'bg-red-50' : ''} flex items-center gap-2 w-full px-4 py-3 text-sm text-red-600`}
                                                        >
                                                            <UserMinus size={16} />
                                                            Remove
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Transition>
                                    </Menu>
                                )}
                            </div>                                         
                        </motion.div>    
                    ))}
                </div>
            )}
        </div>

        <AnimatePresence>
            {isShowConnectionRemove && (
                <StringsRemove getMyStrings={getMyStrings} editConnectionId={editConnectionId}/>
            )}
        </AnimatePresence>
        {isLoading && <LoadingCover/>}
        {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
    </div>
  )
}
